import React, { useState, useEffect, useMemo } from 'react';
import { getDataWithComp } from '../../../../utils/SamuiUtils';

const ArModal = ({ showArModal, handleArClose, type, onRowSelectAr }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [dataList, setDataList] = useState([]);
    const [filteredDataList, setFilteredDataList] = useState([]);
    const [loading, setLoading] = useState(false);

    const typeConfigMap = useMemo(() => ({
        Tb_Set_AR: {
            title: 'รายชื่อลูกค้า',
            columns: [
                { key: 'AR_Code', label: 'รหัสลูกค้า', align: 'center' },
                { key: 'AR_Name', label: 'ชื่อลูกค้า', align: 'start' },
                {
                    key: 'address',
                    label: 'ที่อยู่',
                    align: 'start',
                    render: (item) =>
                        [item.AR_Add1, item.AR_Add2, item.AR_Add3, item.AR_Province, item.AR_ZipCode]
                            .filter(Boolean)
                            .join(' '),
                },
            ],
        },
        API_0701_SO_H: {
            title: 'เอกสารขาย',
            columns: [
                { key: 'Doc_No', label: 'เลขที่เอกสาร', align: 'center' },
                { key: 'AR_Code', label: 'รหัสลูกค้า', align: 'center' },
                { key: 'AR_Name', label: 'ชื่อลูกค้า', align: 'start' },
                {
                    key: 'Product_Name',
                    label: 'ชื่อสินค้า',
                    align: 'start',
                    render: (item) => (
                        <ul style={{ listStyleType: "'- '", paddingLeft: '1rem', textAlign: 'left', margin: 0 }}>
                            {(item.Product_Name || '').split('|||').map((p, idx) => <li key={idx}>{p.trim()}</li>)}
                        </ul>
                    )
                }
            ],
        }
    }), []);


    const config = typeConfigMap[type] || { title: 'ข้อมูล', columns: [] };

    // โหลดข้อมูล
    useEffect(() => {
        if (!showArModal || !typeConfigMap[type]) return;
        const fetchData = async () => {
            if (!showArModal || !typeConfigMap[type]) return;

            setLoading(true);

            try {
                if (type === 'API_0701_SO_H') {
                    const [soHeaderList, soDetailList] = await Promise.all([
                        getDataWithComp('API_0701_SO_H', "ORDER BY Doc_No ASC"),
                        getDataWithComp('API_0702_SO_D', "")
                    ]);

                    const productMap = {};
                    const contactMap = {};

                    soDetailList.forEach(item => {
                        if (!productMap[item.Doc_No]) {
                            productMap[item.Doc_No] = [];
                        }
                        productMap[item.Doc_No].push(item.Item_Name);
                        if (!contactMap[item.Doc_No] && item.AR_Contact1) {
                            contactMap[item.Doc_No] = item.AR_Contact1;
                        }
                        if (item.AR_Contact2) {
                            contactMap[item.Doc_No] = contactMap[item.Doc_No]
                                ? `${contactMap[item.Doc_No]}|||${item.AR_Contact2}`
                                : item.AR_Contact2;
                        }
                    });
                    const mergedList = soHeaderList.map(item => ({
                        ...item,
                        AR_Id: item.AR_ID,
                        Product_Name: (productMap[item.Doc_No] || []).join('|||'),
                        AR_Contact1: contactMap[item.Doc_No] || '',
                        AR_Contact2: contactMap[item.Doc_No] || '',
                    }));
                    console.log("Merged List:", mergedList);
                    setDataList(mergedList);
                    setFilteredDataList(mergedList);
                } else {
                    const list = await getDataWithComp(type, "ORDER BY AR_Code ASC");
                    const cleanList = Array.isArray(list) ? list : [];
                    setDataList(cleanList);
                    setFilteredDataList(cleanList);
                }
            } catch (e) {
                console.error(e);
                setDataList([]);
                setFilteredDataList([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [showArModal, type, typeConfigMap]);

    useEffect(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = dataList.filter(item =>
            config.columns.some(col => {
                const value = item[col.key];
                return value && value.toString().toLowerCase().includes(lowerSearch);
            })
        );
        setFilteredDataList(filtered);
    }, [searchTerm, dataList, config.columns]);

    useEffect(() => {
        if (showArModal) {
            setSearchTerm('');
        }
    }, [showArModal]);

    return (
        <>
            <div className={`modal ${showArModal ? 'show' : ''}`} style={{ display: showArModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{config.title}</h5>
                            <button type="button" className="close" onClick={handleArClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={`ค้นหาใน ${config.columns.map(c => c.label).join(', ')}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            {config.columns.map(col => (
                                                <th key={col.key} className={`text-${col.align || 'center'}`}>{col.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={config.columns.length} className="text-center">
                                                    <div className="spinner-border text-info mt-2" role="status">
                                                        <span className="sr-only text-black">กำลังโหลด...</span>
                                                    </div>
                                                    <div className="text-center mt-2">
                                                        <span className="text-black"> กำลังโหลดข้อมูล...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredDataList.length > 0 ? (
                                            filteredDataList.map((item, index) => (
                                                <tr key={item.Doc_No || item.AR_Code || index} onClick={() => onRowSelectAr(item)} style={{ cursor: 'pointer' }}>
                                                    {config.columns.map(col => (
                                                        <td
                                                            key={col.key}
                                                            className={`text-${col.align || 'center'}`}
                                                            style={{ whiteSpace: 'pre-line' }}
                                                        >
                                                            {col.render ? col.render(item) : item[col.key] || ''}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={config.columns.length} className="text-center">
                                                    <h5>ไม่พบข้อมูล</h5>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={handleArClose}>ปิด</button>
                        </div>
                    </div>
                </div>
            </div>
            {showArModal && <div className="modal-backdrop fade show"></div>}
        </>
    );
};

export default ArModal;
